// react
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';

// material ui
import { Box, Tooltip, Typography } from '@mui/material';
import { green, orange, red, yellow } from '@mui/material/colors';
// types
import type { GridColDef } from '@mui/x-data-grid-premium';

// api
import { useGetMatrikelAnalysisMatrikelAnalysisAccountIdGetQuery } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisMatrikel';

// shared components
import InfoTooltipIcon from '@/components/ui/icon/InfoTooltipIcon';

// feature-components
import OccupancyDaysCell from '@/features/energyxplorer/portfolio/components/datagrid/cells/OccupancyDaysCell';
import TypeIcon from '@/features/energyxplorer/portfolio/components/datagrid/cells/TypeIcon';
// components
import type {
  AnalysisType,
  PortfolioAnalysisOutput,
} from '@/features/energyxplorer/types';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';

// relative
import { formatNumber } from '../../../../utils/formatters';
import EnergyMark from '../components/datagrid/cells/EnergyMark';
import KpiGauge from '../components/datagrid/cells/KpiGauge';
import {
  calculatePercentiles,
  CustomHeaderWithLabel,
  displayTime,
  getOverwrite,
  isGroupRow,
  isLeafRow,
  onlyRenderInParent,
  renderTrend,
  renderTrendOpposite,
  sortComparatorWhenGrouping,
  valueGetterWrapper,
} from '../utils/datagrid-helpers';

export const usePAColumns = (
  transformedData:
    | (PortfolioAnalysisOutput & {
        portfolio_analysis_list: Array<Record<string, unknown>>;
      })
    | null
    | undefined,
  analysisType: AnalysisType,
  presets: Array<Record<string, unknown>> = [],
  chosenPreset: number | null = null,
): GridColDef[] => {
  const account = useSelector(getSelectedAccount);
  const { data: matrikelAnalysisData } =
    useGetMatrikelAnalysisMatrikelAnalysisAccountIdGetQuery(
      { accountId: account?.AccountId },
      { skip: !account?.AccountId },
    );

  const { t } = useTranslation(`portfolio-analysis_${analysisType}`);
  const firstColumns = useMemo(() => {
    const result =
      presets[chosenPreset]?.columnGroupingModel.map((group) => {
        return group.children[0]?.field;
      }) || [];
    result.push('row_actions');
    result.push('warnings');
    result.push('matrikel_actions');
    return result;
  }, [presets, chosenPreset]);

  // Pre calculated percentile values
  const night_day_ratio_percentiles = useMemo(() => {
    return calculatePercentiles(
      transformedData?.portfolio_analysis_list.map((row) => {
        return row.night_day_ratio;
      }),
    );
  }, [transformedData]);

  const weekend_weekday_ratio_percentiles = useMemo(() => {
    return calculatePercentiles(
      transformedData?.portfolio_analysis_list.map((row) => {
        return row.weekend_weekday_ratio;
      }),
    );
  }, [transformedData]);

  const weekend_night_weekday_night_ratio_percentiles = useMemo(() => {
    return calculatePercentiles(
      transformedData?.portfolio_analysis_list.map((row) => {
        return row.weekend_night_weekday_night_ratio;
      }),
    );
  }, [transformedData]);

  const inactive_active_ratio_percentiles = useMemo(() => {
    return calculatePercentiles(
      transformedData?.portfolio_analysis_list.map((row) => {
        return row.inactive_active_ratio;
      }),
    );
  }, [transformedData]);

  // Create field to column group mapping for pivot panel labels
  const fieldToGroupHeader = useMemo(() => {
    const mapping: Record<string, string> = {};
    if (presets && presets.length > 0) {
      // Use the chosen preset's column grouping model (or default to first)
      const presetIndex = chosenPreset ?? 0;
      const columnGroupingModel =
        presets[presetIndex]?.columnGroupingModel || [];
      columnGroupingModel.forEach((group) => {
        const groupHeader = group.headerName;
        group.children?.forEach((child) => {
          if (child.field && groupHeader) {
            mapping[child.field] = groupHeader;
          }
        });
      });
    }
    return mapping;
  }, [presets, chosenPreset]);

  // Get allowed columns from the chosen preset's order array
  const allowedColumns = useMemo(() => {
    if (presets && presets.length > 0 && chosenPreset !== null) {
      const presetIndex = chosenPreset ?? 0;
      const preset = presets[presetIndex];
      // Return the order array as a Set for faster lookup
      const orderSet = preset?.order ? new Set(preset.order) : null;
      const alwaysAllowedColumns = new Set([
        'favorites',
        'meter_id',
        'type',
        'address',
        'matrikel_key',
        'main_address',
        'ExtractedBuildingUse',
      ]);
      return orderSet
        ? new Set([...orderSet, ...alwaysAllowedColumns])
        : alwaysAllowedColumns;
    }
    return null;
  }, [presets, chosenPreset]);

  const columns = useMemo(() => {
    const cols: GridColDef[] = [
      {
        flex: 0,
        aggregable: false,
        sortable: false,
        disableColumnMenu: true,
        field: 'favorites',
        type: 'boolean',
        minWidth: 40,
        width: 40,
        renderCell: (params) => {
          if (params.rowNode.type === 'leaf') {
            return null;
          }
          return !params.value ? (
            <StarBorderRoundedIcon
              sx={{ minWidth: 50, cursor: 'pointer' }}
              color="primary"
            />
          ) : (
            <StarRoundedIcon
              sx={{ minWidth: 50, cursor: 'pointer' }}
              color="warning"
            />
          );
        },
      },
      {
        field: 'type',
        headerName: 'SEU',
        minWidth: 40,
        sortable: false,
        disableColumnMenu: true,
        width: 40,
        flex: 0,
        renderCell: (params) => {
          return <TypeIcon params={params} />;
        },
      },
      {
        field: 'address',
        minWidth: 250,
        type: 'string',
        hideable: false,
        aggregable: false,
        valueGetter: (value, row) => {
          return row.display_name || value;
        },
      },
      {
        field: 'display_name',
        minWidth: 250,
        type: 'string',
        hideable: false,
        aggregable: false,
        valueGetter: (value, row) => {
          return row.display_name || value;
        },
      },
      {
        field: 'matrikel_key',
        type: 'string',
        aggregable: false,
        groupingValueGetter: (params) => {
          let name = '';
          if (params == null) return null;

          transformedData.portfolio_analysis_list.filter((item) => {
            if (item.matrikel_key === params) {
              name = item.matrikel_key;
            }
            return item.matrikel_key === params;
          });
          return name;
        },
        hideable: false,
      },
      {
        field: 'main_address',
        type: 'string',
        aggregable: false,
        hideable: false,
      },
      {
        field: 'average_all',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
        minWidth: 150,
      },
      {
        field: 'last_30_days_average',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
        minWidth: 160,
      },
      {
        field: 'average_heating_season',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
        minWidth: 140,
      },
      {
        field: 'average_non_heating_season',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
        minWidth: 140,
      },
      {
        field: 'average_heating_season_active',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
        minWidth: 210,
      },
      {
        field: 'average_heating_season_inactive',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
        minWidth: 210,
      },
      {
        field: 'last_30_day_trend',
        valueGetter: (value) => {
          return Number(value) * 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value) + '%';
          }
          return formatNumber(value) + '%';
        },
        minWidth: 150,
        renderCell: renderTrend,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'heating_season_trend',
        valueGetter: (value) => {
          return Number(value) * 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value) + '%';
          }
          return formatNumber(value) + '%';
        },
        minWidth: 140,
        renderCell: renderTrend,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'heating_season_trend_active',
        valueGetter: (value) => {
          return Number(value) * 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value) + '%';
          }
          return formatNumber(value) + '%';
        },
        minWidth: 210,
        renderCell: renderTrend,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'heating_season_trend_inactive',
        valueGetter: (value) => {
          return Number(value) * 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value) + '%';
          }
          return formatNumber(value) + '%';
        },
        minWidth: 210,
        renderCell: renderTrend,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'average_all_inactive_hours',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'average_17_to_21',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'average_all_last_year',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'current_calendar_year_avg',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'last_calendar_year_avg',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'average_day',
        minWidth: 160,
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'yearly_trend',
        valueGetter: (value) => {
          return Number(value) * 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value) + '%';
          }
          return formatNumber(value) + '%';
        },
        minWidth: 150,
        renderCell: renderTrend,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'yearly_trend_active',
        valueGetter: (value) => {
          return Number(value) * 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value) + '%';
          }
          return formatNumber(value) + '%';
        },
        minWidth: 150,
        renderCell: renderTrend,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'yearly_trend_inactive',
        valueGetter: (value) => {
          return Number(value) * 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value) + '%';
          }
          return formatNumber(value) + '%';
        },
        minWidth: 150,
        renderCell: renderTrend,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'yoy_ratio',
        valueGetter: (value) => {
          return Number(value) * 100 - 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value) + '%';
          }
          return formatNumber(value) + '%';
        },
        minWidth: 150,
        renderCell: renderTrend,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'yoy_ratio_weekdays',
        valueGetter: (value) => {
          return Number(value) * 100 - 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value) + '%';
          }
          return formatNumber(value) + '%';
        },
        renderCell: renderTrend,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'yoy_ratio_weekday_nights',
        valueGetter: (value) => {
          return Number(value) * 100 - 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value) + '%';
          }
          return formatNumber(value) + '%';
        },
        renderCell: renderTrend,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'yoy_ratio_weekends',
        valueGetter: (value) => {
          return Number(value) * 100 - 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value) + '%';
          }
          return formatNumber(value) + '%';
        },
        renderCell: renderTrend,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'total_consumption_year',
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          return formatNumber(value);
        },
      },
      {
        field: 'total_consumption_last_year',
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          return formatNumber(value);
        },
      },
      {
        field: 'total_consumption_previous_year',
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          return formatNumber(value);
        },
      },
      {
        field: 'current_calendar_year_sum',
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          return formatNumber(value);
        },
      },
      {
        field: 'last_calendar_year_sum',
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          return formatNumber(value);
        },
      },
      {
        field: 'previous_calendar_year_sum',
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          return formatNumber(value);
        },
      },
      {
        field: 'current_calendar_year_consumption_pr_m2_pr_y',
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          return formatNumber(value);
        },
      },
      {
        field: 'last_calendar_year_consumption_pr_m2_pr_y',
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          return formatNumber(value);
        },
      },
      {
        field: 'previous_calendar_year_consumption_pr_m2_pr_y',
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          return formatNumber(value);
        },
      },
      {
        field: 'forward_flow_temp_avg_last_30_days',
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          return formatNumber(value);
        },
      },
      {
        field: 'return_flow_temp_avg_last_30_days',
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          return formatNumber(value);
        },
      },
      {
        field: 'forward_flow_temp_avg_year',
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          return formatNumber(value);
        },
      },
      {
        field: 'return_flow_temp_avg_year',
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          return formatNumber(value);
        },
      },
      {
        field: 'delta_temp_avg_last_30_days',
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          return formatNumber(value);
        },
      },
      {
        field: 'delta_temp_avg_year',
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          return formatNumber(value);
        },
      },
      {
        field: 'delta_temp_trend_last_30_days',
        valueGetter: (value) => {
          return Number(value) * 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value) + '%';
          }
          return formatNumber(value) + '%';
        },
        minWidth: 130,
        renderCell: renderTrendOpposite,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'delta_temp_trend_year',
        valueGetter: (value) => {
          return Number(value) * 100;
        },
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          if (value > 0) {
            return '+' + formatNumber(value) + '%';
          }
          return formatNumber(value) + '%';
        },
        minWidth: 110,
        renderCell: renderTrendOpposite,
        sortComparator: sortComparatorWhenGrouping,
      },
      {
        field: 'average_weekday_full_day',
        minWidth: 90,
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          return formatNumber(Number(value));
        },
      },
      {
        field: 'average_weekday_day',
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          return formatNumber(value);
        },
      },
      {
        field: 'average_weekday_night',
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          return formatNumber(value);
        },
      },
      {
        field: 'average_weekend_day',
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          return formatNumber(value);
        },
      },
      {
        field: 'average_weekend_night',
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          return formatNumber(value);
        },
      },
      {
        field: 'average_weekend_full_day',
        minWidth: 90,
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          return formatNumber(value);
        },
      },

      {
        field: 'weekend_weekday_ratio',
        valueFormatter: (value) => {
          if (value == null) {
            return '-';
          }
          return formatNumber(value);
        },
        renderCell: (params) => {
          // If the pre formatted value is above 1 add an orange arrow up icon and if it is below 1 add a green arrow down icon
          const percentiles = weekend_weekday_ratio_percentiles;

          if (params.value < percentiles[0]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.better75')}>
                  <FiberManualRecordIcon sx={{ color: green[800], mr: 1 }} />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value < percentiles[1]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.better50')}>
                  <FiberManualRecordIcon sx={{ color: yellow[700], mr: 1 }} />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value < percentiles[2]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.worse50')}>
                  <FiberManualRecordIcon sx={{ color: orange[800], mr: 1 }} />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value <= percentiles[3]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.worse75')}>
                  <FiberManualRecordIcon sx={{ color: red[800], mr: 1 }} />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else {
            return <></>;
          }
        },
      },
      {
        field: 'night_day_ratio',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
        renderCell: (params) => {
          // If the pre formatted value is above 1 add an orange arrow up icon and if it is below 1 add a green arrow down icon
          const percentiles = night_day_ratio_percentiles;

          if (params.value < percentiles[0]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.better75')}>
                  <FiberManualRecordIcon sx={{ color: green[800], mr: 1 }} />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value < percentiles[1]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.better50')}>
                  <FiberManualRecordIcon sx={{ color: yellow[700], mr: 1 }} />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value < percentiles[2]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.worse50')}>
                  <FiberManualRecordIcon sx={{ color: orange[800], mr: 1 }} />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value <= percentiles[3]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.worse75')}>
                  <FiberManualRecordIcon sx={{ color: red[800], mr: 1 }} />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else {
            return <></>;
          }
        },
      },
      {
        field: 'inactive_active_ratio',
        minWidth: 100,
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params, 2);
        },
        renderCell: (params) => {
          // If the pre formatted value is above 1 add an orange arrow up icon and if it is below 1 add a green arrow down icon
          const percentiles = inactive_active_ratio_percentiles;

          if (params.value < percentiles[0]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.better75')}>
                  <FiberManualRecordIcon sx={{ color: green[800], mr: 1 }} />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value < percentiles[1]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.better50')}>
                  <FiberManualRecordIcon sx={{ color: yellow[700], mr: 1 }} />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value < percentiles[2]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.worse50')}>
                  <FiberManualRecordIcon sx={{ color: orange[800], mr: 1 }} />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value <= percentiles[3]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.worse75')}>
                  <FiberManualRecordIcon sx={{ color: red[800], mr: 1 }} />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else {
            return <></>;
          }
        },
      },
      {
        field: 'average_night',
        minWidth: 80,
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
      },
      {
        field: 'weekend_night_weekday_night_ratio',
        minWidth: 130,
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params, 2);
        },
        renderCell: (params) => {
          // If the pre formatted value is above 1 add an orange arrow up icon and if it is below 1 add a green arrow down icon
          const percentiles = weekend_night_weekday_night_ratio_percentiles;

          if (params.value < percentiles[0]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.better75')}>
                  <FiberManualRecordIcon sx={{ color: green[800], mr: 1 }} />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value < percentiles[1]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.better50')}>
                  <FiberManualRecordIcon sx={{ color: yellow[700], mr: 1 }} />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value < percentiles[2]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.worse50')}>
                  <FiberManualRecordIcon sx={{ color: orange[800], mr: 1 }} />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else if (params.value <= percentiles[3]) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                }}
              >
                <Tooltip title={t('tooltips.ratio.worse75')}>
                  <FiberManualRecordIcon sx={{ color: red[800], mr: 1 }} />
                </Tooltip>
                {params.formattedValue}
              </Box>
            );
          } else {
            return <></>;
          }
        },
      },
      {
        field: 'occupancy',
        minWidth: 120,
        maxWidth: 120,
        type: 'string',
        aggregable: false,
        renderCell: (params) => <OccupancyDaysCell daysOfWeek={params.value} />,
      },
      {
        field: 'daytime_start',
        minWidth: 80,
        flex: 0.5,
        type: 'number',
        valueFormatter: (value) => displayTime(value),
        renderCell: (params) => {
          return (
            <Box
              sx={{
                fontWeight:
                  params?.row?.daytime_period_source === 'manual' ? 600 : 400,
              }}
            >
              {params.formattedValue}
            </Box>
          );
        },
      },
      {
        field: 'daytime_end',
        minWidth: 80,
        flex: 0.5,
        type: 'number',
        valueFormatter: (value) => displayTime(value),
        renderCell: (params) => {
          return (
            <Box
              sx={{
                fontWeight:
                  params?.row?.daytime_period_source === 'manual' ? 600 : 400,
              }}
            >
              {params.formattedValue}
            </Box>
          );
        },
      },
      {
        field: 'total_co2e_year',
        valueFormatter: (params) => {
          if (params == null) {
            return '';
          }
          return formatNumber(params, 0);
        },
      },
      {
        field: 'kWhPrYPrM2',
        valueFormatter: (params) => {
          if (params == null) {
            return '';
          }
          return formatNumber(params);
        },
        minWidth: 180,
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            let row = params.row;
            if (isGroupRow(params)) {
              const firstChildId = params.rowNode.children[0];
              const firstChild = params.api.getRow(firstChildId);
              row = firstChild;
            }
            return (
              <Box
                sx={{
                  fontWeight: row?.overwrite?.VerificationFlag ? 600 : 400,
                }}
              >
                {params.formattedValue}
              </Box>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
      },
      {
        field: 'BenchmarkCDF',
        minWidth: 180,
        valueFormatter: (params) => {
          if (params == null) {
            return '';
          }
          return 'bund ' + formatNumber(params * 100) + '%';
        },
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            return (
              <Tooltip title={params.formattedValue}>
                <div>
                  <KpiGauge value={params.value} />
                </div>
              </Tooltip>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
      },
      {
        field: 'MedianDiff',
        valueFormatter: (params) => {
          if (params == null) {
            return '';
          }
          return formatNumber(params);
        },
        renderCell: onlyRenderInParent,
        aggregable: false,
      },
      {
        field: 'MeanDiff',
        valueFormatter: (params) => {
          if (params == null) {
            return '';
          }
          return formatNumber(params);
        },
        renderCell: onlyRenderInParent,
        aggregable: false,
      },
      {
        field: 'ExtractedBuildingUse',
        type: 'string',
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            let row = params.row;
            if (isGroupRow(params)) {
              const firstChildId = params.rowNode.children[0];
              const firstChild = params.api.getRow(firstChildId);
              row = firstChild;
            }
            const matrikelData = matrikelAnalysisData?.MatrikelAnalysis?.find(
              (item) => {
                return item.matrikel_key === row.matrikel_key;
              },
            );

            const { value, isOverwritten } = getOverwrite(
              'ExtractedBuildingUse',
              matrikelData,
            );

            row = {
              ...row,
              BuildingUsesWithArea: matrikelData?.BuildingUsesWithArea,
            };
            if (row?.BuildingUsesWithArea == undefined) return null;

            const jsonObject = JSON.parse(
              row?.BuildingUsesWithArea?.replaceAll("'", '"'),
            );
            const result = Object.entries(jsonObject).map(([key, value]) => ({
              key,
              value,
            }));
            if (result.length === 1)
              return (
                <Box
                  sx={{
                    fontWeight: isOverwritten ? 600 : 400,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {value}
                </Box>
              );
            return (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <Box
                    sx={{
                      fontWeight: isOverwritten ? 600 : 400,
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {value}
                  </Box>
                </div>
                <InfoTooltipIcon
                  title={
                    <>
                      {result.map((buildingType, index) => (
                        <div key={index}>
                          <Typography variant="body2">
                            {buildingType.key}: {buildingType.value}{' '}
                            {t('hooks.square_meter', { ns: 'energyxplorer' })}
                          </Typography>
                        </div>
                      ))}
                    </>
                  }
                />
              </div>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
      },
      {
        field: 'AverageBuildingYear',
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            let row = params.row;
            if (isGroupRow(params)) {
              const firstChildId = params.rowNode.children[0];
              const firstChild = params.api.getRow(firstChildId);
              row = firstChild;
            }
            return (
              <Box
                sx={{
                  fontWeight: row?.overwrite?.AverageBuildingYear ? 600 : 400,
                }}
              >
                {params.formattedValue}
              </Box>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
        valueFormatter: (value) => {
          return value?.toFixed(0);
        },
      },
      {
        field: 'SumBuildingArea',
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            let row = params.row;
            if (isGroupRow(params)) {
              const firstChildId = params.rowNode.children[0];
              const firstChild = params.api.getRow(firstChildId);
              row = firstChild;
            }
            return (
              <Box
                sx={{
                  fontWeight: row?.overwrite?.SumBuildingArea ? 600 : 400,
                }}
              >
                {params.formattedValue}
              </Box>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
      },
      {
        field: 'ExtractedHeatInstallation',
        type: 'string',
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            let row = params.row;
            if (isGroupRow(params)) {
              const firstChildId = params.rowNode.children[0];
              const firstChild = params.api.getRow(firstChildId);
              row = firstChild;
            }

            if (row?.HeatInstallationsWithArea == undefined) return null;

            const jsonObject = JSON.parse(
              row.HeatInstallationsWithArea.replaceAll("'", '"'),
            ) as Record<string, number>;
            const result = Object.entries(jsonObject).map(([key, value]) => ({
              key,
              value,
            }));
            if (result.length === 1)
              return (
                <Box
                  sx={{
                    fontWeight: row?.overwrite?.ExtractedHeatInstallation
                      ? 600
                      : 400,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {params.formattedValue}
                </Box>
              );
            return (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  <Box
                    sx={{
                      fontWeight: row?.overwrite?.ExtractedHeatInstallation
                        ? 600
                        : 400,
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {params.formattedValue}
                  </Box>
                </div>
                <InfoTooltipIcon
                  title={
                    <>
                      {result.map((buildingType, index) => (
                        <div key={index}>
                          <Typography variant="body2">
                            {buildingType.key}: {buildingType.value}{' '}
                            {t('hooks.square_meter', { ns: 'energyxplorer' })}
                          </Typography>
                        </div>
                      ))}
                    </>
                  }
                />
              </div>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
      },
      {
        field: 'EnergyLabelClassification',
        renderCell: (params) => {
          if (isGroupRow(params) || isLeafRow(params)) {
            let row = params.row;

            if (params.rowNode.type === 'group') {
              const firstChildId = params.rowNode.children[0];
              const firstChild = params.api.getRow(firstChildId);

              if (!firstChild) return null;

              row = firstChild;
            }

            if (
              row.EnergyLabelCountOverview == null ||
              row.EnergyLabelClassification == null
            )
              return null;

            const jsonObject = JSON.parse(
              row.EnergyLabelCountOverview.replaceAll("'", '"'),
            ) as Record<string, number>;

            const result = Object.entries(jsonObject).map(([key, value]) => ({
              key,
              value,
            }));
            return (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                }}
              >
                <EnergyMark mark={row.EnergyLabelClassification} />

                {result.length > 1 && (
                  <InfoTooltipIcon
                    title={
                      <>
                        <Typography variant="body1">
                          {t('tooltips.energymarksFound')}
                        </Typography>

                        {result.map(({ key, value }) => (
                          <Typography key={key} variant="body2">
                            {key}: {value}
                          </Typography>
                        ))}
                      </>
                    }
                  />
                )}
              </div>
            );
          } else {
            return null;
          }
        },
        aggregable: false,
        type: 'string',
      },
      {
        field: 'warnings',
        type: 'actions',
        aggregable: false,
        hideable: false,
        groupable: false,
        minWidth: 60,
        maxWidth: 60,
        renderCell: (params) => {
          if (params.rowNode.type === 'group') {
            const children = params.rowNode.children
              .map((childId) => params.api.getRow(childId))
              .filter(Boolean);

            if (children.some((r) => !r.meter_in_sync)) {
              return (
                <Tooltip title={t('warnings.rowOutdated')} placement="top">
                  <SyncRoundedIcon
                    color="info"
                    sx={{
                      cursor: 'pointer',
                      marginLeft: 1,
                      animation: 'spin 2s linear infinite',
                      '@keyframes spin': {
                        '0%': {
                          transform: 'rotate(360deg)',
                        },
                        '100%': {
                          transform: 'rotate(0deg)',
                        },
                      },
                    }}
                  />
                </Tooltip>
              );
            } else {
              return null;
            }
          }

          if (!params.row.meter_in_sync) {
            return (
              <Tooltip title={t('warnings.rowOutdated')} placement="top">
                <SyncRoundedIcon
                  color="info"
                  sx={{
                    cursor: 'pointer',
                    marginLeft: 1,
                    animation: 'spin 2s linear infinite',
                    '@keyframes spin': {
                      '0%': {
                        transform: 'rotate(360deg)',
                      },
                      '100%': {
                        transform: 'rotate(0deg)',
                      },
                    },
                  }}
                />
              </Tooltip>
            );
          }
        },
      },
      {
        field: 'average_total_price',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
        minWidth: 140,
      },
      {
        field: 'average_nighttime_price',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
        minWidth: 140,
      },
      {
        field: 'total_cost_year',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
        minWidth: 120,
      },
      {
        field: 'total_cost_last_year',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
        minWidth: 120,
      },
      {
        field: 'total_cost_previous_year',
        valueFormatter: (params) => {
          if (params == null) {
            return '-';
          }
          return formatNumber(params);
        },
        minWidth: 140,
      },
    ];

    return cols
      .map((column: GridColDef): GridColDef => {
        const origValueGetter = column.valueGetter;
        const baseHeaderName = t(`columns.${column.field}.header`);
        const groupHeader = t(fieldToGroupHeader[column.field]);
        // Include group name in header for better pivot panel context
        const headerName = groupHeader
          ? `${groupHeader}: ${baseHeaderName}`
          : baseHeaderName;

        return {
          flex: 1,
          type: 'number',
          minWidth: 140,
          groupable: false,
          ...column,
          renderHeader: (params) => (
            <CustomHeaderWithLabel props={params} t={t} />
          ),
          headerName,

          valueGetter: (value, row, column, api) => {
            return valueGetterWrapper(
              value,
              row,
              column,
              api,
              origValueGetter,
              matrikelAnalysisData,
              analysisType,
            );
          },
          cellClassName: firstColumns.includes(column.field)
            ? 'cell-left-border'
            : '',
        };
      })
      .filter((column) => {
        // Filter by allowed columns from the chosen preset's order array
        if (allowedColumns && !allowedColumns.has(column.field)) {
          return false;
        }

        // Analysis type specific filters
        if (analysisType === 'water')
          return !['average_17_to_21'].includes(column.field);
        if (analysisType === 'heat')
          return !['average_17_to_21'].includes(column.field);
        return true;
      }) as GridColDef[];
  }, [
    transformedData,
    weekend_weekday_ratio_percentiles,
    t,
    night_day_ratio_percentiles,
    fieldToGroupHeader,
    inactive_active_ratio_percentiles,
    weekend_night_weekday_night_ratio_percentiles,
    firstColumns,
    matrikelAnalysisData,
    analysisType,
    allowedColumns,
  ]);

  return columns;
};
